import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, Globe, Check, ShieldCheck, AlertTriangle, Code2, Copy, Rocket, ChevronDown, Palette } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";
import LinkFormModal from "@/pages/partner/LinkFormModal";

// Compact INR formatter — 4.2L, 25K, 850 — used for at-a-glance row badges.
const formatInrShort = (n) => {
    const v = Number(n) || 0;
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(v >= 100000000 ? 0 : 1)}Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(v >= 1000000 ? 0 : 1)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K`;
    return `₹${v}`;
};

/**
 * Reusable payment-links manager UI.
 * `scope`: "admin" -> uses /admin/partners/{pid}/payment-links + /admin/payment-links/...
 *          "partner" -> uses /partner/payment-links + /partner/payment-links/...
 * `partnerId`: required when scope === "admin"
 */
export default function PaymentLinksList({ scope, partnerId, partnerBusinessName }) {
    const [links, setLinks] = useState([]);
    const [channels, setChannels] = useState({ upi: [], bank: [] });
    const [websites, setWebsites] = useState([]);
    const [stats, setStats] = useState({});  // {link_code: {pending, today_success_count, today_value}}
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const baseLink = scope === "admin"
        ? `/admin/partners/${partnerId}/payment-links`
        : `/partner/payment-links`;
    const linkOps = scope === "admin"
        ? (id) => `/admin/payment-links/${id}`
        : (id) => `/partner/payment-links/${id}`;
    const channelsPath = scope === "admin"
        ? `/admin/partners/${partnerId}/channels`
        : `/partner/channels`;
    // Websites only available on partner scope today (no admin proxy yet).
    const websitesPath = scope === "partner" ? "/partner/websites" : null;
    // Stats only ship for the partner self-view today; admin scope shows
    // an empty stats object so the badges simply don't render.
    const statsPath = scope === "partner" ? "/partner/payment-links/stats" : null;

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2, r3, r4] = await Promise.all([
                api.get(baseLink),
                api.get(channelsPath).catch(() => ({ data: { upi_channels: [], bank_accounts: [] } })),
                statsPath ? api.get(statsPath).catch(() => ({ data: { stats: {} } })) : Promise.resolve({ data: { stats: {} } }),
                websitesPath ? api.get(websitesPath).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
            ]);
            setLinks(r1.data.items || []);
            setChannels({
                upi: r2.data.upi_channels || [],
                bank: r2.data.bank_accounts || [],
            });
            setStats(r3.data.stats || {});
            setWebsites(r4.data.items || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load();   }, [scope, partnerId]);

    const remove = async (link) => {
        if (link.is_legacy) return alert("Cannot delete the default link — disable it instead.");
        if (!window.confirm(`Delete link "${link.link_code}"? This cannot be undone.`)) return;
        await api.delete(linkOps(link.link_id));
        load();
    };

    const verify = async (link) => {
        try {
            const r = await api.post(`${linkOps(link.link_id)}/verify-domain`);
            const { ok, reason, expected_target } = r.data;
            if (ok) alert(`✅ Verified.\n${reason}`);
            else alert(`❌ Not yet verified.\n\nExpected CNAME target: ${expected_target}\nReason: ${reason}\n\nAdd a CNAME record from ${link.custom_domain} → ${expected_target} at your DNS registrar, then wait 1–5 minutes and click Verify again.`);
            load();
        } catch (e) {
            alert(formatDetail(e?.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <p className="text-xs text-slate-500">
                    Each link gets its own /pay URL with custom branding, redirect URLs and channel allowlist.
                </p>
                <button data-testid="new-link-btn" onClick={() => setEditing("new")}
                        className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> New link
                </button>
            </div>

            {loading ? (
                <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block text-blue-600" /></div>
            ) : (
                <div className="space-y-2">
                    {links.length === 0 && <div className="text-center text-sm text-slate-400 py-6">No payment links yet.</div>}
                    {links.map((l) => (
                        <LinkRow key={l.link_id} link={l}
                                 stats={stats[l.link_code]}
                                 onEdit={() => setEditing(l)}
                                 onDelete={() => remove(l)}
                                 onVerify={() => verify(l)} />
                    ))}
                </div>
            )}

            {editing && (
                <LinkFormModal
                    scope={scope}
                    baseLink={baseLink}
                    linkOps={linkOps}
                    fallbackName={partnerBusinessName}
                    link={editing === "new" ? null : editing}
                    channels={channels}
                    websites={websites}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function LinkRow({ link, stats, onEdit, onDelete, onVerify }) {
    const [showEmbed, setShowEmbed] = useState(false);
    const [copied, setCopied] = useState(false);
    const liveUrl = `${window.location.origin}/pay/${link.link_code}`;
    const restricted = (link.allowed_upi_channels?.length || 0) + (link.allowed_bank_accounts?.length || 0) > 0;
    const ds = link.domain_status;
    const pending = stats?.pending || 0;
    const todayCount = stats?.today_success_count || 0;
    const todayValue = stats?.today_value || 0;
    const copyLiveUrl = (e) => {
        e.preventDefault(); e.stopPropagation();
        const ok = (text) => { setCopied(true); setTimeout(() => setCopied(false), 1400); };
        try {
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(liveUrl).then(() => ok(liveUrl)).catch(() => fallback());
                return;
            }
        } catch { /* fall through */ }
        const fallback = () => {
            const ta = document.createElement("textarea");
            ta.value = liveUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
            document.body.appendChild(ta); ta.select();
            try { document.execCommand("copy"); ok(liveUrl); } catch { /* swallow */ }
            ta.remove();
        };
        fallback();
    };
    return (
        <div data-testid={`link-row-${link.link_code}`}
             className="border border-slate-200 rounded-md p-3 hover:bg-slate-50/60 transition flex items-center justify-between gap-3">
            {link.logo_data_url && (
                <img src={link.logo_data_url} alt="" className="w-10 h-10 rounded-md object-contain bg-white border border-slate-200 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{link.display_name}</span>
                    <span className={`pill ${link.status === "active" ? "pill-success" : "pill-failed"}`}>{link.status}</span>
                    {link.is_legacy && <span className="pill pill-info">default</span>}
                    {restricted && <span className="pill pill-muted">channel allowlist</span>}
                    {link.enabled_methods === "upi_only" && <span className="pill pill-info">UPI only</span>}
                    {link.enabled_methods === "bank_only" && <span className="pill pill-info">Bank only</span>}
                    {link.custom_domain && ds === "dns_verified" && (
                        <span className="pill pill-success flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> domain live</span>
                    )}
                    {link.custom_domain && ds === "dns_pending" && (
                        <span className="pill pill-pending flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> dns pending</span>
                    )}
                    {pending > 0 && (
                        <span data-testid={`stat-pending-${link.link_code}`}
                              title={`${pending} submitted ${pending === 1 ? "transaction" : "transactions"} awaiting approval`}
                              className="pill pill-pending flex items-center gap-1">
                            {pending} pending
                        </span>
                    )}
                    {todayCount > 0 && (
                        <span data-testid={`stat-today-count-${link.link_code}`}
                              title={`${todayCount} successful ${todayCount === 1 ? "transaction" : "transactions"} today (IST)`}
                              className="pill pill-success flex items-center gap-1">
                            {todayCount} today
                        </span>
                    )}
                    {todayValue > 0 && (
                        <span data-testid={`stat-today-value-${link.link_code}`}
                              title={`Total successful volume today (IST): ₹${todayValue.toLocaleString("en-IN")}`}
                              className="pill pill-muted flex items-center gap-1 font-mono">
                            {formatInrShort(todayValue)}
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono flex items-center gap-2 flex-wrap">
                    <a href={liveUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        /pay/{link.link_code} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                    {link.custom_domain && (
                        <span className="flex items-center gap-1 text-emerald-700">
                            <Globe className="w-2.5 h-2.5" /> {link.custom_domain}
                        </span>
                    )}
                </div>
                {(link.success_redirect || link.failure_redirect) && (
                    <div className="text-[10px] text-slate-400 mt-1 truncate">
                        {link.success_redirect && <>→ success: <span className="font-mono">{link.success_redirect}</span></>}
                        {link.success_redirect && link.failure_redirect && " · "}
                        {link.failure_redirect && <>→ failure: <span className="font-mono">{link.failure_redirect}</span></>}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {link.custom_domain && link.domain_status === "dns_verified" && (
                    <div className="group relative flex items-center">
                        <a data-testid={`test-live-${link.link_code}`}
                           href={`https://${link.custom_domain}`}
                           target="_blank" rel="noreferrer"
                           title={`Open https://${link.custom_domain} in a new tab to verify the redirect to /pay/${link.link_code}`}
                           className="border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 px-2 py-1 rounded text-[11px] font-medium flex items-center gap-1 transition-colors">
                            <Rocket className="w-3 h-3" /> Test live
                        </a>
                        <button data-testid={`copy-canonical-${link.link_code}`}
                                onClick={copyLiveUrl}
                                title={`Copy canonical fallback URL: ${liveUrl}`}
                                className="ml-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 px-1.5 py-1 rounded text-[10px] font-medium flex items-center gap-1">
                            {copied ? (<><Check className="w-3 h-3 text-emerald-600" /> Copied</>) : (<><Copy className="w-3 h-3" /> /pay URL</>)}
                        </button>
                    </div>
                )}
                {link.custom_domain && link.domain_status !== "dns_verified" && (
                    <div className="group relative flex items-center">
                        <span data-testid={`test-live-disabled-${link.link_code}`}
                              title="Verify DNS first to enable the live test button"
                              className="border border-slate-200 bg-slate-50 text-slate-400 px-2 py-1 rounded text-[11px] font-medium flex items-center gap-1 cursor-not-allowed">
                            <Rocket className="w-3 h-3" /> Test live
                        </span>
                        <button data-testid={`copy-canonical-${link.link_code}`}
                                onClick={copyLiveUrl}
                                title={`Copy canonical fallback URL: ${liveUrl}`}
                                className="ml-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 px-1.5 py-1 rounded text-[10px] font-medium flex items-center gap-1">
                            {copied ? (<><Check className="w-3 h-3 text-emerald-600" /> Copied</>) : (<><Copy className="w-3 h-3" /> /pay URL</>)}
                        </button>
                    </div>
                )}
                <button data-testid={`embed-link-${link.link_code}`} title="Get embed code"
                        onClick={() => setShowEmbed(true)}
                        className="text-indigo-700 hover:bg-indigo-50 p-1.5 rounded">
                    <Code2 className="w-3.5 h-3.5" />
                </button>
                {/* Tawk.to-style customizer — separate page so we have room
                    for the form + live preview side-by-side. */}
                <Link to={`/partner/links/${link.link_id}/embed`}
                      data-testid={`customize-embed-${link.link_code}`}
                      title="Customize widget (colors, position, threshold)"
                      className="text-fuchsia-700 hover:bg-fuchsia-50 p-1.5 rounded">
                    <Palette className="w-3.5 h-3.5" />
                </Link>
                {link.custom_domain && (
                    <button data-testid={`verify-link-${link.link_code}`} title="Verify DNS" onClick={onVerify}
                            className="text-emerald-700 hover:bg-emerald-50 p-1.5 rounded">
                        <ShieldCheck className="w-3.5 h-3.5" />
                    </button>
                )}
                <button data-testid={`edit-link-${link.link_code}`} onClick={onEdit}
                        className="text-slate-600 hover:bg-slate-100 p-1.5 rounded">
                    <Pencil className="w-3.5 h-3.5" />
                </button>
                {!link.is_legacy && (
                    <button data-testid={`delete-link-${link.link_code}`} onClick={onDelete}
                            className="text-rose-600 hover:bg-rose-50 p-1.5 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            {showEmbed && <EmbedSnippetModal linkCode={link.link_code} onClose={() => setShowEmbed(false)} />}
        </div>
    );
}


function EmbedSnippetModal({ linkCode, onClose }) {
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        api.get(`/public/embed-snippet/${linkCode}`).then((r) => setData(r.data));
    }, [linkCode]);
    const copy = () => {
        if (!data?.snippet) return;
        navigator.clipboard.writeText(data.snippet);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };
    return (
        <Modal testId="embed-snippet-modal" onClose={onClose}
               title={`Embed checkout · ${linkCode}`} size="lg">
            {!data ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div> : (
                <div className="space-y-4 text-sm">
                    <p className="text-slate-600">
                        Paste this snippet on your site to launch the payment modal — customers
                        stay on your domain. Pre-fill the amount per button via{" "}
                        <code className="font-mono bg-slate-100 px-1 rounded">data-paygate-amount</code>.
                    </p>
                    <div className="relative">
                        <pre data-testid="embed-snippet-code" className="bg-slate-900 text-slate-100 text-xs font-mono p-4 rounded-md overflow-x-auto whitespace-pre-wrap leading-relaxed">{data.snippet}</pre>
                        <button onClick={copy}
                                data-testid="embed-snippet-copy"
                                className="absolute top-2 right-2 bg-slate-700 hover:bg-slate-600 text-white p-1.5 rounded text-xs flex items-center gap-1">
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs leading-relaxed">
                        <strong>Programmatic API:</strong> open the modal from any code path with
                        <code className="font-mono bg-white px-1 rounded mx-1">window.PayGate.open(&#123;amount: 2500, username: &apos;ORDER123&apos;&#125;)</code>.
                        Add <code className="font-mono bg-white px-1 rounded">data-paygate-username</code> to associate orders.
                    </div>
                </div>
            )}
        </Modal>
    );
}

